import { useRef, useState } from "react";
import Icon from "./Icon";
import { siteDetails } from "../siteContent";

const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT ||
  "https://ascot.onrender.com/send-email";

const initialFormData = {
  name: "",
  email: "",
  telephone: "",
  enquiryType: "",
  message: "",
  consent: false,
};

export const validateContactForm = (values) => {
  const errors = {};

  if (!values.name.trim()) {
    errors.name = "Please enter your name.";
  }

  if (!values.email.trim()) {
    errors.email = "Please enter your email address.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = "Please enter a valid email address.";
  }

  if (!values.telephone.trim()) {
    errors.telephone = "Please enter a telephone number.";
  } else if (!/^[+()\d\s-]{7,20}$/.test(values.telephone)) {
    errors.telephone = "Please enter a valid telephone number.";
  }

  if (!values.enquiryType) {
    errors.enquiryType = "Please choose an enquiry type.";
  }

  if (!values.message.trim()) {
    errors.message = "Please tell us how we can help.";
  } else if (values.message.trim().length < 10) {
    errors.message = "Please add a little more detail (at least 10 characters).";
  }

  if (!values.consent) {
    errors.consent = "Please confirm we may use these details to reply.";
  }

  return errors;
};

const Contact = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const statusRef = useRef(null);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    const nextValue = type === "checkbox" ? checked : value;

    setFormData((current) => ({ ...current, [name]: nextValue }));
    setErrors((current) => ({ ...current, [name]: undefined }));
    if (status !== "idle") {
      setStatus("idle");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === "loading") return;

    const validationErrors = validateContactForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const firstInvalidField = event.currentTarget.querySelector(
        `[name="${Object.keys(validationErrors)[0]}"]`
      );
      firstInvalidField?.focus();
      return;
    }

    setStatus("loading");

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          telephone: formData.telephone.trim(),
          enquiryType: formData.enquiryType,
          message: formData.message.trim(),
          consent: formData.consent,
        }),
      });

      if (!response.ok) {
        throw new Error("The contact service returned an error.");
      }

      setFormData(initialFormData);
      setErrors({});
      setStatus("success");
    } catch {
      setStatus("error");
    } finally {
      window.requestAnimationFrame(() => statusRef.current?.focus());
    }
  };

  const fieldError = (name) =>
    errors[name] ? (
      <span className="field-error" id={`${name}-error`}>
        {errors[name]}
      </span>
    ) : null;

  return (
    <section className="contact-section" id="contact">
      <div className="container contact-layout">
        <div className="contact-details">
          <p className="eyebrow">Contact Ascot Lodge</p>
          <h2>Start with a conversation</h2>
          <p className="lead-copy">
            Whether you are planning ahead or need support soon, the team can
            talk through your questions and help you understand the next step.
          </p>

          <div className="contact-list">
            <a href={siteDetails.phoneHref}>
              <span className="icon-tile">
                <Icon name="phone" size={24} />
              </span>
              <span>
                <small>Call the home</small>
                <strong>{siteDetails.phoneDisplay}</strong>
              </span>
            </a>
            <a href={`mailto:${siteDetails.email}`}>
              <span className="icon-tile">
                <Icon name="mail" size={24} />
              </span>
              <span>
                <small>Email us</small>
                <strong>{siteDetails.email}</strong>
              </span>
            </a>
            <a href={siteDetails.mapsUrl} rel="noreferrer" target="_blank">
              <span className="icon-tile">
                <Icon name="location" size={24} />
              </span>
              <span>
                <small>Visit us</small>
                <strong>{siteDetails.addressInline}</strong>
              </span>
            </a>
          </div>
        </div>

        <div className="form-card">
          <div className="form-card__heading">
            <p className="eyebrow">Send an enquiry</p>
            <h3>How can we help?</h3>
            <p>Required fields are marked with an asterisk (*).</p>
          </div>

          <form noValidate onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="name">Name *</label>
                <input
                  aria-describedby={errors.name ? "name-error" : undefined}
                  aria-invalid={Boolean(errors.name)}
                  autoComplete="name"
                  id="name"
                  name="name"
                  onChange={handleChange}
                  required
                  type="text"
                  value={formData.name}
                />
                {fieldError("name")}
              </div>

              <div className="form-field">
                <label htmlFor="email">Email address *</label>
                <input
                  aria-describedby={errors.email ? "email-error" : undefined}
                  aria-invalid={Boolean(errors.email)}
                  autoComplete="email"
                  id="email"
                  inputMode="email"
                  name="email"
                  onChange={handleChange}
                  required
                  type="email"
                  value={formData.email}
                />
                {fieldError("email")}
              </div>

              <div className="form-field">
                <label htmlFor="telephone">Telephone *</label>
                <input
                  aria-describedby={
                    errors.telephone ? "telephone-error" : undefined
                  }
                  aria-invalid={Boolean(errors.telephone)}
                  autoComplete="tel"
                  id="telephone"
                  inputMode="tel"
                  name="telephone"
                  onChange={handleChange}
                  required
                  type="tel"
                  value={formData.telephone}
                />
                {fieldError("telephone")}
              </div>

              <div className="form-field">
                <label htmlFor="enquiryType">Enquiry type *</label>
                <select
                  aria-describedby={
                    errors.enquiryType ? "enquiryType-error" : undefined
                  }
                  aria-invalid={Boolean(errors.enquiryType)}
                  id="enquiryType"
                  name="enquiryType"
                  onChange={handleChange}
                  required
                  value={formData.enquiryType}
                >
                  <option value="">Please choose</option>
                  <option value="care">Care enquiry</option>
                  <option value="visit">Arrange a visit</option>
                  <option value="general">General question</option>
                  <option value="careers">Careers</option>
                </select>
                {fieldError("enquiryType")}
              </div>

              <div className="form-field form-field--full">
                <label htmlFor="message">Message *</label>
                <textarea
                  aria-describedby={errors.message ? "message-error" : undefined}
                  aria-invalid={Boolean(errors.message)}
                  id="message"
                  name="message"
                  onChange={handleChange}
                  required
                  rows="5"
                  value={formData.message}
                />
                {fieldError("message")}
              </div>

              <div className="form-field form-field--full checkbox-field">
                <input
                  aria-describedby={errors.consent ? "consent-error" : undefined}
                  aria-invalid={Boolean(errors.consent)}
                  checked={formData.consent}
                  id="consent"
                  name="consent"
                  onChange={handleChange}
                  required
                  type="checkbox"
                />
                <label htmlFor="consent">
                  I agree that Ascot Lodge may use these details to respond to
                  my enquiry. *
                </label>
                {fieldError("consent")}
              </div>
            </div>

            <p className="form-privacy">
              Please do not include detailed medical information in this form.
              Your details will be used to respond to your enquiry.
            </p>

            <button className="button form-submit" disabled={status === "loading"} type="submit">
              {status === "loading" ? "Sending…" : "Send enquiry"}
              {status !== "loading" && <Icon name="arrow" size={20} />}
            </button>

            <div
              aria-live="polite"
              className={`form-status form-status--${status}`}
              ref={statusRef}
              role={status === "error" ? "alert" : "status"}
              tabIndex="-1"
            >
              {status === "success" && (
                <>
                  <Icon name="check" size={21} />
                  <span>Thank you. Your enquiry has been sent to Ascot Lodge.</span>
                </>
              )}
              {status === "error" && (
                <>
                  <span>
                    We could not send your enquiry. Please try again or call{" "}
                    <a href={siteDetails.phoneHref}>{siteDetails.phoneDisplay}</a>.
                  </span>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
